import React, { useContext, useEffect, useRef, useState } from "react";
import { Button, Col, Drawer, Menu, Modal, Row, Tag } from "antd";
import {
  CloseOutlined,
  EyeOutlined,
  ShoppingCartOutlined,
} from "@ant-design/icons";
import CurrencyFormatter from "../../../components/CurrencyFormatter";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AppContext } from "../../../context/AppContextProvider";
import { getQRproducts } from "../../../network/network";
import { TransactionStatus } from "../../../configs/constants";
import {
  QR_LANGUAGES,
  applyQrLanguage,
  qrLangShort,
  readStoredQrLocale,
  restoreQrLanguage,
} from "./qrLanguage";
import { shortOrderNumber } from "./qrOrderNumber";
import { handleApiError } from "../../../utils/errorHandler";

const QRorderHistory = () => {
  const { appConfig, setAppConfigWrapper } = useContext(AppContext);

  const [backLink, setBackLink] = useState("");
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [companyDetails, setCompanyDetails] = useState({});
  const [modalVisible, setModalVisible] = useState(false);
  const [modalData, setModalData] = useState({});
  const { t, i18n } = useTranslation("qr");
  const navigate = useNavigate();
  const requestStartedRef = useRef(false);
  const redirectStartedRef = useRef(false);
  const setAppConfigWrapperRef = useRef(setAppConfigWrapper);
  const translationRef = useRef(t);

  translationRef.current = t;

  // ---------- Language init from localStorage ----------
  useEffect(() => {
    restoreQrLanguage(setAppConfigWrapperRef.current);
  }, []);

  // ---------- Load company & orders ----------
  useEffect(() => {
    if (requestStartedRef.current) return;
    requestStartedRef.current = true;

    const src = localStorage.getItem("src");
    const table = localStorage.getItem("table");
    const isDriveThru = localStorage.getItem("is_drive_thru");

    if (src && table && isDriveThru !== null) {
      getQRproducts({ src, table })
        .then((response) => {
          const data = response.data || response.response?.data;
          if (data?.success) {
            setCompanyDetails(data.data);
          } else {
            handleApiError(response, translationRef.current);
          }
        })
        .catch((error) => handleApiError(error, translationRef.current));

      setBackLink(
        `/qr/ordering?src=${src}&table=${table}&is_drive_thru=${isDriveThru}`,
      );
    }
  }, []);

  // ---------- If no orders, go back to ordering ----------
  useEffect(() => {
    if (companyDetails && Array.isArray(companyDetails.orders)) {
      if (
        companyDetails.orders.length === 0 &&
        backLink &&
        !redirectStartedRef.current
      ) {
        redirectStartedRef.current = true;
        navigate(backLink);
      }
    }
  }, [companyDetails, backLink, navigate]);

  return (
    <div>
      {companyDetails.orders?.length > 0 && (
        <div style={{ direction: appConfig.direction }}>
          {/* Header */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              columnGap: "10px",
              boxShadow: "var(--color-shadow-soft) 5px 10px 80px 0px",
              padding: "10px",
            }}
          >
            <div
              className="app-flex-align-center-gap-10"
            >
              <img
                alt={companyDetails.company_name}
                src={
                  companyDetails.logo
                    ? companyDetails.logo
                    : "/images/image-not-found.png"
                }
                style={{
                  width: "100%",
                  maxWidth: "40px",
                  height: "auto",
                  overflow: "hidden",
                  borderRadius: "35rem",
                }}
              />
              <div>
                <div
                  style={{
                    fontSize: "12px",
                    color: "var(--color-text-muted)",
                    lineHeight: "1",
                  }}
                >
                  {t("welcome_to")}
                </div>
                <div
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    textTransform: "capitalize",
                    maxWidth: "150px",
                    lineHeight: "1",
                    marginTop: "3px",
                  }}
                >
                  {companyDetails.company_name}
                </div>
              </div>
            </div>

            <div className="app-flex-gap-10">
              <Button
                type="primary"
                onClick={() => {
                  setDrawerVisible(true);
                }}
                style={{ padding: "0", width: "40px", height: "40px" }}
              >
                {qrLangShort(i18n.language)}
              </Button>
            </div>
          </div>

          {/* Body */}
          <div style={{ padding: "10px" }}>
            <h3>{t("your_order")}</h3>

            <Row style={{ paddingBottom: "70px" }} gutter={[16, 16]}>
              {companyDetails.orders.map((order) => {
                const isPending =
                  order.status.value === TransactionStatus.Pending;
                const isInProgress =
                  order.status.value === TransactionStatus.InProgress;

                const statusBg = isPending
                  ? "var(--color-warning-shadow)"
                  : isInProgress
                    ? "var(--color-info-shadow)"
                    : "var(--color-bg)";

                const statusColor = isPending
                  ? "var(--color-warning-hover)"
                  : isInProgress
                    ? "var(--color-info-hover)"
                    : "var(--color-text)";

                const borderColor = isInProgress
                  ? "var(--color-info)"
                  : isPending
                    ? "var(--color-warning)"
                    : "var(--color-border)";

                const itemCount = order.items
                  ? order.items.reduce(
                      (sum, item) => sum + (item.quantity || 0),
                      0,
                    )
                  : 0;

                const itemLabel = `${itemCount} ${t("items") || "items"}`;

                return (
                  <Col
                    xs={24}
                    sm={12}
                    md={12}
                    lg={8}
                    xl={6}
                    xxl={6}
                    key={order.id}
                  >
                    <div
                      style={{
                        padding: "16px",
                        borderRadius: 18,
                        background: "var(--color-white)",
                        border: `2px solid ${borderColor}`,
                        boxShadow: "0 6px 15px var(--color-shadow-soft)",
                        display: "flex",
                        flexDirection: "column",
                        rowGap: 12,
                      }}
                    >
                      {/* status badge */}
                      <div
                        style={{
                          padding: "6px 12px",
                          borderRadius: 50,
                          background: statusBg,
                          color: statusColor,
                          fontWeight: 700,
                          fontSize: 13,
                          alignSelf: "flex-start",
                        }}
                      >
                        {order.status.name}
                      </div>

                      {/* order id */}
                      <div
                        style={{ fontSize: 14, fontWeight: 600, color: "var(--color-heading)" }}
                      >
                        {t("order_id")}: {shortOrderNumber(order.order_id)}
                      </div>

                      {/* amount */}
                      <div style={{ fontSize: 18, fontWeight: 800 }}>
                        {t("amount")}:{" "}
                        <span style={{ color: "var(--color-primary)" }}>
                          <CurrencyFormatter value={order.amount_charged} />
                        </span>
                      </div>

                      {/* items count */}
                      <div
                        style={{
                          fontSize: 14,
                          fontWeight: 500,
                          color: "var(--color-text)",
                          marginTop: -4,
                        }}
                      >
                        {itemLabel}
                      </div>

                      {/* view button */}
                      <div style={{ marginTop: 6 }}>
                        <Button
                          type="primary"
                          size="large"
                          style={{
                            width: "100%",
                            height: 42,
                            fontWeight: 600,
                            borderRadius: 12,
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            columnGap: 8,
                          }}
                          onClick={() => {
                            setModalVisible(true);
                            setModalData(order);
                          }}
                        >
                          <EyeOutlined />
                          {t("view_details")}
                        </Button>
                      </div>
                    </div>
                  </Col>
                );
              })}
            </Row>

            {/* bottom bar */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                columnGap: "10px",
                boxShadow: "1px -2px 4px 0 var(--color-shadow-soft)",
                padding: "10px",
                position: "fixed",
                bottom: "0",
                left: "0",
                right: "0",
                backgroundColor: "var(--color-white)",
              }}
            >
              <Button
                type="primary"
                style={{
                  padding: "0 10px",
                  height: "40px",
                  lineHeight: "1",
                  display: " flex",
                  alignItems: "center",
                  columnGap: "7px",
                }}
                onClick={() => {
                  navigate(backLink);
                }}
              >
                <span>{t("place_new_order")}</span>
                <ShoppingCartOutlined
                  style={{
                    fontSize: "25px",
                    lineHeight: "0",
                    color: "var(--color-white)",
                  }}
                />
              </Button>
            </div>
          </div>

          {/* Drawer */}
          <Drawer
            title={t("select_language")}
            placement={appConfig.direction === "rtl" ? "left" : "right"}
            onClose={() => {
              setDrawerVisible(false);
            }}
            open={drawerVisible}
            styles={{
              body: { padding: "0" },
            }}
            closable={false}
            extra={
              <Button
                type="text"
                danger
                icon={<CloseOutlined />}
                onClick={() => {
                  setDrawerVisible(false);
                }}
              />
            }
          >
            <Menu
              selectedKeys={[readStoredQrLocale() || "en"]}
              items={QR_LANGUAGES.map((language) => ({
                key: language.code,
                label: language.label,
                onClick: () => {
                  applyQrLanguage(language.code, setAppConfigWrapper);
                  setDrawerVisible(false);
                },
              }))}
            />
          </Drawer>
        </div>
      )}

      {/* Order details modal */}
      <Modal
        open={modalVisible}
        title={null}
        onOk={() => setModalVisible(false)}
        onCancel={() => setModalVisible(false)}
        footer={null}
      >
        {modalData.items?.length > 0 && (
          <div className="app-text-xs">
            <div
              style={{
                fontWeight: "bold",
                padding: "5px 0",
              }}
            >
              {t("order_id")}
            </div>
            <div className="app-bordered-inline">
              {modalData.order_id ? shortOrderNumber(modalData.order_id) : "-"}
            </div>

            <div
              style={{
                fontWeight: "bold",
                padding: "5px 0",
              }}
            >
              {t("status")}
            </div>
            <div className="app-bordered-inline">
              {modalData.status?.value === TransactionStatus.Pending && (
                <Tag color="warning">{modalData.status.name}</Tag>
              )}
              {modalData.status?.value === TransactionStatus.InProgress && (
                <Tag color="blue">{modalData.status.name}</Tag>
              )}
            </div>

            {modalData.items && (
              <>
                <div
                  style={{
                    fontWeight: "bold",
                    padding: "5px 0",
                  }}
                >
                  {t("items")}
                </div>
                <div
                  className="app-bordered-inline"
                >
                  {modalData.items?.map((item) => (
                    <div key={"product_items_" + item.id}>
                      {appConfig.direction === "rtl" ? (
                        <>
                          {item.quantity} x <b>{item.name}</b>
                        </>
                      ) : (
                        <>
                          {item.quantity} x <b>{item.name_en}</b>
                        </>
                      )}
                      <br />
                      {/* Show tags if exist */}
                      {item.tags && item.tags.length > 0 && (
                        <ul
                          style={{
                            margin: "2px 0 0 16px",
                            padding: 0,
                            listStyle: "none",
                          }}
                        >
                          {item.tags.map((tag) => (
                            <li key={tag.id} className="app-text-xs">
                              -{" "}
                              {appConfig.direction === "ltr"
                                ? tag.name
                                : tag.name_ar}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>

                <div
                  style={{
                    fontWeight: "bold",
                    padding: "5px 0",
                  }}
                >
                  {t("total")}
                </div>
                <div
                  className="app-bordered-inline"
                >
                  <CurrencyFormatter value={modalData.amount_charged} />
                </div>
              </>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default QRorderHistory;
